import React, { useEffect, useState } from "react";
import Layout from "@/components/Layout";
import { api, colones, initials } from "@/lib/api";
import { Users, Plus, X, Pencil, Trash2, Search, User as UserIcon, Briefcase, DollarSign, Eye, Camera, Upload, FileDown, Palmtree } from "lucide-react";
import EmployeeDetailModal from "@/components/EmployeeDetailModal";
import TabVacacionesEmpleado from "@/components/TabVacacionesEmpleado";
import CambioEfectivoDialog from "@/components/CambioEfectivoDialog";
import HistorialCampoEmpleado from "@/components/HistorialCampoEmpleado";
import { diffVersionedFields, nuevoCambioId } from "@/lib/empleadoVersioning";
import { tablaRenta, exentoRenta, calcularRentaMensual } from "@/lib/renta";
import { ACTIVIDAD_JORNADAS_ESPECIALES } from "@/lib/payroll";
import { compressImage } from "@/lib/image";
import ReportModal from "@/components/ReportModal";
import { reporteEmpleados } from "@/lib/reportes";

const TABS = [
  { id: "personal", label: "Personal", icon: UserIcon },
  { id: "laboral", label: "Laboral", icon: Briefcase },
  { id: "financiero", label: "Financiero", icon: DollarSign },
  { id: "vacaciones", label: "Vacaciones", icon: Palmtree },
];

const RentaEmpleadoCard = ({ data, set, setData, employee, empresa, onSaved }) => {
  const tabla = tablaRenta(empresa, new Date().toISOString().slice(0, 10));
  const sujeto = (Number(data.salario_base) || 0) > exentoRenta(tabla);
  const est = calcularRentaMensual(Number(data.salario_base) || 0, tabla, { conyuge: !!data.renta_conyuge, hijos: data.renta_hijos || 0 });
  const hist = (field) => employee?.id && <HistorialCampoEmpleado empleado={data} field={field} onUpdated={(updated) => { setData(updated); onSaved?.(); }} />;
  return (
    <div className={`md:col-span-2 p-4 rounded-xl border ${sujeto ? "border-[#D96C4A] bg-[#FAF2EE]" : "border-c"}`} data-testid="emp-renta-card">
      <div className="font-medium text-sm">Impuesto sobre la Renta</div>
      {sujeto && <div className="text-xs text-error font-medium mt-0.5" data-testid="emp-renta-sujeto">Este salario está sujeto al impuesto</div>}
      <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="flex items-center gap-2 text-sm cursor-pointer mt-6">
            <input type="checkbox" checked={!!data.renta_conyuge} onChange={e => set("renta_conyuge", e.target.checked)} data-testid="emp-renta-conyuge" />
            Tiene cónyuge (sin ingresos propios)
          </label>
          {hist("renta_conyuge")}
        </div>
        <div><label className="np-label">Cantidad de hijos</label>
          <input type="number" min="0" step="1" className="np-input" value={data.renta_hijos ?? 0} onChange={e => set("renta_hijos", Math.max(0, parseInt(e.target.value, 10) || 0))} data-testid="emp-renta-hijos" />
          {hist("renta_hijos")}
        </div>
        <div><label className="np-label">Cuándo se rebaja</label>
          <select className="np-input" value={data.renta_modo || "quincena"} onChange={e => set("renta_modo", e.target.value)} data-testid="emp-renta-modo">
            <option value="quincena">En cada quincena</option>
            <option value="segunda">Solo en la II quincena</option>
          </select>
          {hist("renta_modo")}
        </div>
      </div>
      <div className="text-xs text-secondary-c mt-3" data-testid="emp-renta-estimado">
        Impuesto estimado del mes con el salario base: <strong className="text-primary-c">{colones(est.impuestoMes)}</strong>
      </div>
    </div>
  );
};

const SalarioCuentasCard = ({ data, setData, employee, onSaved }) => {
  const sc = data.salario_cuentas || {};
  const upd = (patch) => setData(p => {
    const next = { ...(p.salario_cuentas || {}), ...patch };
    const total = (Number(next.c1_monto) || 0) + (Number(next.c2_monto) || 0);
    return { ...p, salario_cuentas: next, ...(next.activo ? { salario_base: total } : {}) };
  });
  const m1 = Number(sc.c1_monto) || 0, m2 = Number(sc.c2_monto) || 0, tot = m1 + m2;
  const pct = (m) => tot > 0 ? `${((m / tot) * 100).toFixed(1)}%` : "—";
  return (
    <div className={`md:col-span-2 p-4 rounded-xl border ${sc.activo ? "border-primary-c bg-[#E6EFEA]" : "border-c"}`} data-testid="emp-salario-cuentas-card">
      <label className="flex items-start gap-3 cursor-pointer">
        <input type="checkbox" checked={!!sc.activo} onChange={e => upd({ activo: e.target.checked })} className="mt-0.5" data-testid="emp-salario-dividido" />
        <div>
          <div className="font-medium text-sm">Salario dividido en dos cuentas</div>
          <div className="text-xs text-secondary-c">El salario base es la suma de dos salarios. La planilla se calcula con el total y en el Reporte de Pago y el comprobante se reparte por cuenta.</div>
        </div>
      </label>
      {sc.activo && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
          {[1, 2].map(n => (
            <div key={n} className="grid grid-cols-2 gap-2">
              <div><label className="np-label">Nombre cuenta {n}</label><input className="np-input" value={sc[`c${n}_nombre`] || ""} onChange={e => upd({ [`c${n}_nombre`]: e.target.value })} placeholder={n === 1 ? "Cuenta BN" : "Cuenta BAC"} data-testid={`emp-cuenta${n}-nombre`} /></div>
              <div><label className="np-label">Monto {n}</label>
                <div className="relative"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c">₡</span>
                  <input type="number" min="0" className="np-input pl-7" required value={sc[`c${n}_monto`] || ""} onChange={e => upd({ [`c${n}_monto`]: Number(e.target.value) })} data-testid={`emp-cuenta${n}-monto`} />
                </div>
              </div>
            </div>
          ))}
          <div className="md:col-span-2 text-sm" data-testid="emp-salario-cuentas-total">
            Salario base total: <strong>{colones(tot)}</strong>
            <span className="text-xs text-secondary-c ml-2">{sc.c1_nombre || "Cuenta 1"} {pct(m1)} · {sc.c2_nombre || "Cuenta 2"} {pct(m2)}</span>
          </div>
        </div>
      )}
      {employee?.id && <HistorialCampoEmpleado empleado={data} field="salario_cuentas" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
    </div>
  );
};

const EmployeeModal = ({ employee, empresa = {}, onClose, onSaved }) => {
  const [tab, setTab] = useState("personal");
  const [data, setData] = useState(employee || {
    estado: "Activo", jornada: "Diurna", frecuencia_pago: "Mensual",
    ccss_aplica: true, almuerzo_paga: true,
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingDiffs, setPendingDiffs] = useState(null); // diffs pendientes de confirmar fecha efectiva
  // Prompt para fecha CCSS: aparece al marcar el check cuando aún no hay fecha guardada
  const [ccssPrompt, setCcssPrompt] = useState("idle"); // idle | ask | other
  const fileRef = React.useRef(null);
  const set = (k, v) => setData(p => ({ ...p, [k]: v }));

  const todayISO = new Date().toISOString().slice(0, 10);

  const handleCcssToggle = (checked) => {
    set("ccss_aplica", checked);
    if (checked && !data.fecha_ccss) {
      setCcssPrompt("ask");
    } else if (!checked) {
      // Si se desmarca, limpiar prompt (la fecha persiste por si lo vuelve a marcar)
      setCcssPrompt("idle");
    }
  };

  const onAvatarPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { alert("Solo se permiten imágenes (JPG, PNG, WebP)."); return; }
    if (file.size > 8 * 1024 * 1024) { alert("La imagen debe pesar menos de 8 MB."); return; }
    setUploading(true);
    try {
      const dataUrl = await compressImage(file, { maxDim: 400, quality: 0.82 });
      set("avatar", dataUrl);
    } catch {
      alert("No se pudo procesar la imagen.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async (e) => {
    e.preventDefault();
    // Si estamos EDITANDO (hay empleado previo), detectamos cambios en campos versionados.
    // Si los hay, abrimos el diálogo de fecha efectiva ANTES de persistir.
    if (employee?.id) {
      const diffs = diffVersionedFields(employee, data);
      if (diffs.length > 0) {
        setPendingDiffs(diffs);
        return;
      }
    }
    await doSave(data);
  };

  const doSave = async (payload) => {
    setSaving(true);
    try {
      const cleanPayload = { ...payload, dias_vacaciones: null, saldo_inicial_fecha: null };
      if (employee?.id) await api.put(`/empleados/${employee.id}`, cleanPayload);
      else await api.post("/empleados", cleanPayload);
      onSaved();
    } finally { setSaving(false); }
  };

  const onConfirmCambios = async (confirmed) => {
    // Appendeamos nuevas entradas al array __cambios con las fechas elegidas
    const prevCambios = Array.isArray(employee?.__cambios) ? employee.__cambios : [];
    const nowIso = new Date().toISOString();
    const nuevos = confirmed.map(c => ({
      id: nuevoCambioId(),
      field: c.field,
      old_value: c.old_value,
      new_value: c.new_value,
      effective_from: c.effective_from,
      created_at: nowIso,
    }));
    const merged = [...prevCambios, ...nuevos];
    setPendingDiffs(null);
    await doSave({ ...data, __cambios: merged });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto shadow-2xl fade-in-up" onClick={(e) => e.stopPropagation()} data-testid="employee-modal">
        <div className="p-6 border-b border-c flex items-center justify-between">
          <h3 className="font-display text-2xl font-semibold">{employee ? "Editar Empleado" : "Nuevo Empleado"}</h3>
          <button onClick={onClose} className="np-btn np-btn-ghost p-2"><X size={18} /></button>
        </div>

        <div className="flex gap-1 px-6 pt-5 border-b border-c">
          {TABS.map(t => {
            const Ic = t.icon;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${tab === t.id ? "border-[var(--primary)] text-primary-color" : "border-transparent text-secondary-c hover:text-primary-c"}`}>
                <Ic size={16} /> {t.label}
              </button>
            );
          })}
        </div>

        <form onSubmit={save} className="p-6 space-y-4">
          {tab === "personal" && (
            <div className="space-y-5">
              <div className="flex items-center gap-5 p-4 rounded-2xl bg-[#FAFAF7] border border-c">
                <div className="relative w-20 h-20 flex-shrink-0">
                  {data.avatar ? (
                    <img src={data.avatar} alt="" className="w-20 h-20 rounded-2xl object-cover ring-1 ring-[var(--border)]" data-testid="emp-avatar-preview" />
                  ) : (
                    <div className="w-20 h-20 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center font-display text-xl font-semibold">
                      {(data.nombre || "?")[0]?.toUpperCase()}{(data.apellidos || "")[0]?.toUpperCase()}
                    </div>
                  )}
                  {uploading && <div className="absolute inset-0 rounded-2xl bg-black/40 flex items-center justify-center"><div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full spinner" /></div>}
                </div>
                <div className="flex-1">
                  <div className="font-medium text-sm">Foto del empleado</div>
                  <div className="text-xs text-secondary-c mt-0.5">JPG, PNG o WebP. Se redimensiona automáticamente a 400 px.</div>
                  <div className="flex gap-2 mt-2.5">
                    <input ref={fileRef} type="file" accept="image/*" onChange={onAvatarPick} className="hidden" data-testid="emp-avatar-input" />
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="np-btn np-btn-outline text-xs py-1.5 px-3" data-testid="emp-avatar-upload-btn">
                      <Upload size={13} /> {data.avatar ? "Cambiar foto" : "Subir foto"}
                    </button>
                    {data.avatar && (
                      <button type="button" onClick={() => set("avatar", "")} className="np-btn np-btn-ghost text-xs py-1.5 px-3 text-error" data-testid="emp-avatar-remove-btn">
                        <Trash2 size={13} /> Quitar
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="np-label">Código</label><input className="np-input" value={data.codigo || ""} onChange={e => set("codigo", e.target.value)} /></div>
              <div><label className="np-label">Cédula *</label><input className="np-input" required value={data.cedula || ""} onChange={e => set("cedula", e.target.value)} data-testid="emp-cedula" /></div>
              <div><label className="np-label">Nombre *</label><input className="np-input" required value={data.nombre || ""} onChange={e => set("nombre", e.target.value)} data-testid="emp-nombre" /></div>
              <div><label className="np-label">Apellidos *</label><input className="np-input" required value={data.apellidos || ""} onChange={e => set("apellidos", e.target.value)} data-testid="emp-apellidos" /></div>
              <div><label className="np-label">Fecha de nacimiento</label><input type="date" className="np-input" value={data.fecha_nacimiento || ""} onChange={e => set("fecha_nacimiento", e.target.value)} /></div>
              <div><label className="np-label">Género</label>
                <select className="np-input" value={data.genero || ""} onChange={e => set("genero", e.target.value)}>
                  <option value="">Seleccionar...</option><option>Masculino</option><option>Femenino</option><option>Otro</option>
                </select>
              </div>
              <div><label className="np-label">Estado civil</label>
                <select className="np-input" value={data.estado_civil || ""} onChange={e => set("estado_civil", e.target.value)}>
                  <option value="">Seleccionar...</option><option>Soltero/a</option><option>Casado/a</option><option>Divorciado/a</option><option>Viudo/a</option><option>Unión libre</option>
                </select>
              </div>
              <div><label className="np-label">Teléfono</label><input className="np-input" value={data.telefono || ""} onChange={e => set("telefono", e.target.value)} /></div>
              <div className="md:col-span-2"><label className="np-label">Correo electrónico</label><input type="email" className="np-input" value={data.correo || ""} onChange={e => set("correo", e.target.value)} /></div>
              <div className="md:col-span-2"><label className="np-label">Dirección</label><input className="np-input" value={data.direccion || ""} onChange={e => set("direccion", e.target.value)} /></div>

              <div className="md:col-span-2 mt-2 pt-4 border-t border-c">
                <div className="text-xs uppercase tracking-widest text-secondary-c font-semibold mb-3">Contacto de emergencia</div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div><label className="np-label">Nombre completo</label><input className="np-input" value={data.contacto_emergencia || ""} onChange={e => set("contacto_emergencia", e.target.value)} placeholder="Ej: María Pérez" /></div>
                  <div><label className="np-label">Parentesco</label>
                    <select className="np-input" value={data.parentesco_emergencia || ""} onChange={e => set("parentesco_emergencia", e.target.value)}>
                      <option value="">Seleccionar...</option>
                      <option>Cónyuge</option>
                      <option>Padre</option>
                      <option>Madre</option>
                      <option>Hijo/a</option>
                      <option>Hermano/a</option>
                      <option>Abuelo/a</option>
                      <option>Tío/a</option>
                      <option>Primo/a</option>
                      <option>Amigo/a</option>
                      <option>Otro</option>
                    </select>
                  </div>
                  <div><label className="np-label">Teléfono</label><input className="np-input" value={data.telefono_emergencia || ""} onChange={e => set("telefono_emergencia", e.target.value)} placeholder="Ej: 8888-8888" /></div>
                </div>
              </div>
            </div>
            </div>
          )}

          {tab === "laboral" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="np-label">Departamento</label>
                {Array.isArray(empresa.departamentos) && empresa.departamentos.length > 0 ? (
                  <select
                    className="np-input"
                    value={data.departamento || ""}
                    onChange={e => set("departamento", e.target.value)}
                    data-testid="emp-departamento"
                  >
                    <option value="">Seleccionar...</option>
                    {empresa.departamentos.map(d => <option key={d} value={d}>{d}</option>)}
                    {data.departamento && !empresa.departamentos.includes(data.departamento) && (
                      <option value={data.departamento}>{data.departamento} (personalizado)</option>
                    )}
                  </select>
                ) : (
                  <>
                    <input className="np-input" value={data.departamento || ""} onChange={e => set("departamento", e.target.value)} data-testid="emp-departamento" placeholder="Definí los departamentos en Empresa" />
                    <div className="text-[11px] text-secondary-c mt-1">Configurá los departamentos en la sección <strong>Empresa</strong>.</div>
                  </>
                )}
              </div>
              <div>
                <label className="np-label">Puesto *</label>
                {Array.isArray(empresa.puestos) && empresa.puestos.length > 0 ? (
                  <select
                    className="np-input"
                    required
                    value={data.puesto || ""}
                    onChange={e => set("puesto", e.target.value)}
                    data-testid="emp-puesto"
                  >
                    <option value="">Seleccionar...</option>
                    {empresa.puestos.map(p => (
                      <option key={p.nombre} value={p.nombre}>
                        {p.nombre}{p.no_horas_extras ? " (sin horas extras)" : ""}
                      </option>
                    ))}
                    {data.puesto && !empresa.puestos.some(p => p.nombre === data.puesto) && (
                      <option value={data.puesto}>{data.puesto} (personalizado)</option>
                    )}
                  </select>
                ) : (
                  <>
                    <input className="np-input" required value={data.puesto || ""} onChange={e => set("puesto", e.target.value)} data-testid="emp-puesto" placeholder="Definí los puestos en Empresa" />
                    <div className="text-[11px] text-secondary-c mt-1">Configurá los puestos en la sección <strong>Empresa</strong>.</div>
                  </>
                )}
                {employee?.id && <HistorialCampoEmpleado empleado={data} field="puesto" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
              </div>
              <div><label className="np-label">Fecha de ingreso *</label><input type="date" className="np-input" required value={data.fecha_ingreso || ""} onChange={e => set("fecha_ingreso", e.target.value)} /></div>
              <div><label className="np-label">Jornada *</label>
                <select className="np-input" value={data.jornada || "Diurna"} onChange={e => set("jornada", e.target.value)}>
                  <option>Diurna</option><option>Mixta</option><option>Nocturna</option><option>Rotativa</option>
                </select>
                {employee?.id && <HistorialCampoEmpleado empleado={data} field="jornada" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
              </div>
              <div><label className="np-label">Estado</label>
                <select className="np-input" value={data.estado || "Activo"} onChange={e => set("estado", e.target.value)}>
                  <option>Activo</option><option>Inactivo</option><option>Suspendido</option><option>Despedido</option>
                </select>
              </div>

              <div className="md:col-span-2 space-y-3">
                <label className="flex items-start gap-3 p-4 rounded-xl border border-c cursor-pointer hover:bg-[#FAFAF7]">
                  <input type="checkbox" checked={!!data.ccss_aplica} onChange={e => handleCcssToggle(e.target.checked)} className="mt-0.5" data-testid="emp-ccss-check" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">CCSS</div>
                    <div className="text-xs text-secondary-c">Aplica rebajo del 10.83% del salario bruto (cuota obrero)</div>
                    {data.ccss_aplica && data.fecha_ccss && (
                      <div className="text-xs text-success mt-1.5">Fecha de ingreso a CCSS: <strong>{data.fecha_ccss}</strong></div>
                    )}
                    {employee?.id && <HistorialCampoEmpleado empleado={data} field="ccss_aplica" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
                  </div>
                </label>

                {data.ccss_aplica && ccssPrompt === "ask" && (
                  <div className="p-4 rounded-xl bg-[#FFF7E5] border border-[#F0CC8C] space-y-3" data-testid="ccss-prompt">
                    <div className="text-sm">¿La fecha de ingreso a la CCSS de este empleado es <strong>hoy ({todayISO})</strong>?</div>
                    <div className="flex gap-2 flex-wrap">
                      <button type="button" onClick={() => { set("fecha_ccss", todayISO); setCcssPrompt("idle"); }} className="np-btn np-btn-primary text-sm py-1.5" data-testid="ccss-use-today">Sí, usar hoy</button>
                      <button type="button" onClick={() => setCcssPrompt("other")} className="np-btn np-btn-outline text-sm py-1.5" data-testid="ccss-pick-other">Elegir otra fecha</button>
                    </div>
                  </div>
                )}

                {data.ccss_aplica && ccssPrompt === "other" && (
                  <div className="p-4 rounded-xl bg-[#FAFAF7] border border-c space-y-3">
                    <div className="text-sm font-medium">Seleccioná la fecha real de ingreso a la CCSS:</div>
                    <input
                      type="date"
                      max={todayISO}
                      value={data.fecha_ccss || ""}
                      onChange={e => set("fecha_ccss", e.target.value)}
                      className="np-input w-full md:w-auto"
                      data-testid="ccss-date-input"
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setCcssPrompt("idle")} disabled={!data.fecha_ccss} className="np-btn np-btn-primary text-sm py-1.5 disabled:opacity-50" data-testid="ccss-confirm-date">Confirmar</button>
                      <button type="button" onClick={() => setCcssPrompt("ask")} className="np-btn np-btn-ghost text-sm py-1.5">Volver</button>
                    </div>
                  </div>
                )}

                {data.ccss_aplica && data.fecha_ccss && ccssPrompt === "idle" && (
                  <button type="button" onClick={() => setCcssPrompt("other")} className="text-xs text-primary-c underline ml-7" data-testid="ccss-change-date">Cambiar fecha de ingreso a CCSS</button>
                )}

                <label className="flex items-start gap-3 p-4 rounded-xl border border-c cursor-pointer hover:bg-[#FAFAF7]">
                  <input type="checkbox" checked={!!data.almuerzo_paga} onChange={e => set("almuerzo_paga", e.target.checked)} className="mt-0.5" />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Hora de almuerzo paga</div>
                    <div className="text-xs text-secondary-c">Si no está marcado, se descuenta 1h del cálculo de horas en asistencia</div>
                    {employee?.id && <HistorialCampoEmpleado empleado={data} field="almuerzo_paga" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer hover:bg-[#FAFAF7] ${data.pensionado ? "border-primary-c bg-[#E6EFEA]" : "border-c"}`}>
                  <input
                    type="checkbox"
                    checked={!!data.pensionado}
                    onChange={e => set("pensionado", e.target.checked)}
                    className="mt-0.5"
                    data-testid="emp-pensionado"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-sm">Es pensionado (cotiza CCSS reducida)</div>
                    <div className="text-xs text-secondary-c">
                      El trabajador ya recibe pensión del IVM. Al marcar esta opción, la deducción CCSS se calcula con la tasa reducida configurada en Empresa (por defecto <strong>5,50 %</strong>) en lugar del 10,83 %.
                    </div>
                    {employee?.id && <HistorialCampoEmpleado empleado={data} field="pensionado" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer hover:bg-[#FAFAF7] ${data.planilla_editable ? "border-primary-c bg-[#E6EFEA]" : "border-c"}`}>
                  <input
                    type="checkbox"
                    checked={!!data.planilla_editable}
                    onChange={e => set("planilla_editable", e.target.checked)}
                    className="mt-0.5"
                    data-testid="emp-planilla-editable"
                  />
                  <div>
                    <div className="font-medium text-sm">Planilla editable</div>
                    <div className="text-xs text-secondary-c">
                      Permite ajustar manualmente las horas ordinarias, extras y feriado del empleado directamente en la Planilla. La asistencia registrada se usa como valor inicial pero podés modificarla antes de generar el comprobante.
                    </div>
                  </div>
                </label>

                <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer hover:bg-[#FAFAF7] ${data.reportar_medio_tiempo ? "border-primary-c bg-[#E6EFEA]" : "border-c"}`}>
                  <input
                    type="checkbox"
                    checked={!!data.reportar_medio_tiempo}
                    onChange={e => set("reportar_medio_tiempo", e.target.checked)}
                    className="mt-0.5"
                    data-testid="emp-reportar-medio-tiempo"
                  />
                  <div>
                    <div className="font-medium text-sm">Reportar a medio tiempo</div>
                    <div className="text-xs text-secondary-c">
                      En el <strong>Reporte de Planillas</strong> (Excel mensual) los montos ganados del empleado se muestran a la mitad. Las demás hojas y las planillas semanales/quincenales/mensuales conservan el salario completo.
                    </div>
                  </div>
                </label>

                {(empresa.actividad_grupo === "servicios_jornadas_especiales" || data.jornada_especial_48) && (
                  <label className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer hover:bg-[#FAFAF7] ${data.jornada_especial_48 ? "border-primary-c bg-[#E6EFEA]" : "border-c"}`} data-testid="emp-jornada-especial-48-card">
                    <input
                      type="checkbox"
                      checked={!!data.jornada_especial_48}
                      onChange={e => set("jornada_especial_48", e.target.checked)}
                      className="mt-0.5"
                      data-testid="emp-jornada-especial-48"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-sm">Jornada especial: extras solo al superar 48 h semanales</div>
                      <div className="text-xs text-secondary-c">
                        Para personal administrativo o de enfermería. En planillas quincenales y mensuales, las horas trabajadas de lunes a domingo se pagan como ordinarias hasta <strong>48 h</strong>; solo el excedente se paga como hora extra. No aplica a la planilla semanal.
                      </div>
                      {employee?.id && <HistorialCampoEmpleado empleado={data} field="jornada_especial_48" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
                    </div>
                  </label>
                )}
              </div>

              <div className="md:col-span-2"><label className="np-label">Notas</label><textarea className="np-input" rows={3} value={data.notas || ""} onChange={e => set("notas", e.target.value)} /></div>
            </div>
          )}

          {tab === "financiero" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><label className="np-label">Salario base *</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c">₡</span>
                  <input type="number" className="np-input pl-7 disabled:bg-[#F3F0E8] disabled:cursor-not-allowed" required disabled={!!data.salario_cuentas?.activo} value={data.salario_base || ""} onChange={e => set("salario_base", Number(e.target.value))} data-testid="emp-salario" />
                </div>
                {data.salario_cuentas?.activo && <div className="text-[11px] text-secondary-c mt-1">Se calcula como la suma de las dos cuentas.</div>}
                {employee?.id && <HistorialCampoEmpleado empleado={data} field="salario_base" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
              </div>
              <SalarioCuentasCard data={data} setData={setData} employee={employee} onSaved={onSaved} />
              {empresa.actividad_grupo === ACTIVIDAD_JORNADAS_ESPECIALES && String(data.frecuencia_pago || "Mensual").toLowerCase() !== "semanal" && <RentaEmpleadoCard data={data} set={set} setData={setData} employee={employee} empresa={empresa} onSaved={onSaved} />}
              <div><label className="np-label">Frecuencia de pago</label>
                <select className="np-input" value={data.frecuencia_pago || "Mensual"} onChange={e => set("frecuencia_pago", e.target.value)}>
                  <option>Semanal</option><option>Quincenal</option><option>Mensual</option>
                </select>
                {employee?.id && <HistorialCampoEmpleado empleado={data} field="frecuencia_pago" onUpdated={(updated) => { setData(updated); onSaved?.(); }} />}
              </div>
              <div><label className="np-label">Banco</label><input className="np-input" value={data.banco || ""} onChange={e => set("banco", e.target.value)} /></div>
              <div><label className="np-label">Cuenta bancaria (IBAN)</label><input className="np-input" value={data.cuenta_bancaria || ""} onChange={e => set("cuenta_bancaria", e.target.value)} /></div>
              <div className="md:col-span-2"><label className="np-label">Número seguro social</label><input className="np-input" value={data.numero_seguro_social || ""} onChange={e => set("numero_seguro_social", e.target.value)} /></div>
            </div>
          )}

          {tab === "vacaciones" && (
            <TabVacacionesEmpleado data={data} empresa={empresa} onChange={set} />
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-c">
            <button type="button" onClick={onClose} className="np-btn np-btn-ghost" data-testid="emp-cancel">Cancelar</button>
            <button type="submit" disabled={saving} className="np-btn np-btn-primary" data-testid="emp-submit">{saving ? "Guardando..." : (employee ? "Guardar" : "Crear empleado")}</button>
          </div>
        </form>
      </div>
      {pendingDiffs && (
        <CambioEfectivoDialog
          diffs={pendingDiffs}
          onConfirm={onConfirmCambios}
          onCancel={() => setPendingDiffs(null)}
        />
      )}
    </div>
  );
};

export default function Empleados() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(null); // null | "new" | {employee}
  const [viewing, setViewing] = useState(null); // employee to view in detail
  const [showReport, setShowReport] = useState(false);
  const [empresa, setEmpresa] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusTab, setStatusTab] = useState("activos"); // activos | inactivos

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/empleados");
    setItems(data);
    try { const { data: e } = await api.get("/empresa"); setEmpresa(e || {}); } catch (err) { console.error("Empleados: no se pudo cargar empresa", err); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const isActivo = (e) => (e.estado || "Activo") === "Activo";
  const activosCount = items.filter(isActivo).length;
  const inactivosCount = items.length - activosCount;

  const filtered = items.filter(e => {
    const q = search.toLowerCase();
    const matchStatus = statusTab === "activos" ? isActivo(e) : !isActivo(e);
    if (!matchStatus) return false;
    return !q || `${e.nombre} ${e.apellidos} ${e.cedula} ${e.puesto} ${e.departamento}`.toLowerCase().includes(q);
  });

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este empleado?")) return;
    await api.delete(`/empleados/${id}`);
    load();
  };

  return (
    <Layout>
      <header className="flex items-end justify-between mb-8 flex-wrap gap-4 fade-in-up">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-2xl bg-[#E6EFEA] text-primary-color flex items-center justify-center"><Users size={26} strokeWidth={1.5} /></div>
          <div>
            <h1 className="font-display text-3xl font-semibold">Empleados</h1>
            <p className="text-sm text-secondary-c">{items.length} {items.length === 1 ? "empleado registrado" : "empleados registrados"}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowReport(true)} className="np-btn np-btn-outline" data-testid="empleados-report-btn"><FileDown size={16} className="mr-1.5" />Reporte</button>
          <button onClick={() => setOpen("new")} className="np-btn np-btn-primary" data-testid="new-employee-btn"><Plus size={16} /> Nuevo Empleado</button>
        </div>
      </header>

      {showReport && (
        <ReportModal
          title="Reporte de Empleados" mode="simple"
          onClose={() => setShowReport(false)}
          onGenerate={({ format }) => reporteEmpleados({ format, empleados: items, empresa })}
        />
      )}

      <div className="np-card mb-5 p-4">
        <div className="flex items-center gap-1 mb-4 border-b border-c -mx-4 -mt-4 px-4 pt-1">
          <button
            onClick={() => setStatusTab("activos")}
            data-testid="emp-tab-activos"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${statusTab === "activos" ? "border-[var(--primary)] text-primary-color" : "border-transparent text-secondary-c hover:text-primary-c"}`}
          >
            Activos
            <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${statusTab === "activos" ? "bg-[#E6EFEA] text-primary-color" : "bg-[#F0F0EA] text-secondary-c"}`}>
              {activosCount}
            </span>
          </button>
          <button
            onClick={() => setStatusTab("inactivos")}
            data-testid="emp-tab-inactivos"
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors -mb-px ${statusTab === "inactivos" ? "border-[var(--primary)] text-primary-color" : "border-transparent text-secondary-c hover:text-primary-c"}`}
          >
            Inactivos
            <span className={`inline-flex items-center justify-center min-w-[22px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${statusTab === "inactivos" ? "bg-[#E6EFEA] text-primary-color" : "bg-[#F0F0EA] text-secondary-c"}`}>
              {inactivosCount}
            </span>
          </button>
        </div>
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-secondary-c" />
          <input className="np-input pl-9" placeholder="Buscar empleados..." value={search} onChange={e => setSearch(e.target.value)} data-testid="search-employees" />
        </div>
      </div>

      <div className="np-card p-0 np-scroll">
        {loading ? <div className="p-10 text-center text-secondary-c">Cargando...</div> : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="emp-empty-state">
            <Users size={36} className="mx-auto text-secondary-c opacity-40 mb-3" />
            <div className="font-medium">
              {statusTab === "activos" ? "No hay empleados activos" : "No hay empleados inactivos"}
            </div>
            <div className="text-sm text-secondary-c">
              {statusTab === "activos"
                ? (items.length === 0 ? "Empezá creando tu primer empleado." : "Cambiá a la pestaña “Inactivos” para verlos.")
                : "Cuando un empleado pase a Inactivo, Suspendido o Despedido aparecerá acá."}
            </div>
          </div>
        ) : (
          <table className="np-table">
            <thead><tr>
              <th>Empleado</th><th>Cédula</th><th>Puesto</th><th>Jornada</th><th>Salario</th><th>Estado</th><th></th>
            </tr></thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.id} data-testid={`employee-row-${e.cedula}`}>
                  <td className="cursor-pointer" onClick={() => setViewing(e)}>
                    <div className="flex items-center gap-3">
                      {e.avatar ? <img src={e.avatar} alt="" className="w-9 h-9 rounded-full object-cover" />
                        : <div className="w-9 h-9 rounded-full bg-[#E6EFEA] text-primary-color flex items-center justify-center text-xs font-semibold">{initials(e.nombre + " " + (e.apellidos||""))}</div>}
                      <div>
                        <div className="font-medium hover:text-primary-color transition-colors">{e.nombre} {e.apellidos}</div>
                        <div className="text-xs text-secondary-c">{e.departamento || "—"}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-secondary-c">{e.cedula}</td>
                  <td>{e.puesto}</td>
                  <td><span className="np-badge np-badge-warning">{e.jornada}</span></td>
                  <td className="font-medium">{colones(e.salario_base)}</td>
                  <td><span className={`np-badge ${e.estado === "Activo" ? "np-badge-success" : "np-badge-neutral"}`}>{e.estado}</span></td>
                  <td>
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setViewing(e)} className="np-btn np-btn-ghost p-2" data-testid={`view-emp-${e.cedula}`} title="Ver detalles"><Eye size={15} /></button>
                      <button onClick={() => setOpen({ employee: e })} className="np-btn np-btn-ghost p-2" data-testid={`edit-emp-${e.cedula}`} title="Editar"><Pencil size={15} /></button>
                      <button onClick={() => remove(e.id)} className="np-btn np-btn-ghost p-2 text-error" data-testid={`delete-emp-${e.cedula}`} title="Eliminar"><Trash2 size={15} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <EmployeeModal employee={open === "new" ? null : open.employee} empresa={empresa} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); load(); }} />
      )}

      {viewing && (
        <EmployeeDetailModal empleado={viewing} empresa={empresa} onClose={() => setViewing(null)} />
      )}
    </Layout>
  );
}
